import React, { useState } from 'react';
import { Save, X, Package, DollarSign, Hash, BarChart3, AlertCircle, Image } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import ImageUpload from '../ui/ImageUpload';
import toast from 'react-hot-toast';

const ProductForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    cost: initialData?.cost || '',
    category: initialData?.category || '',
    barcode: initialData?.barcode || '',
    stock_quantity: initialData?.stock_quantity || '',
    min_stock: initialData?.min_stock || '',
    active: initialData?.active !== undefined ? initialData.active : true,
    unit: initialData?.unit || 'unidade',
    image: null,
    image_url: initialData?.image_url || null
  });

  const [errors, setErrors] = useState({});

  const categories = [
    'Lanches',
    'Bebidas',
    'Salgados',
    'Doces',
    'Sucos',
    'Refrigerantes',
    'Água',
    'Snacks',
    'Outros'
  ];

  const units = [
    'unidade',
    'litro',
    'ml',
    'kg',
    'g',
    'pacote',
    'caixa',
    'garrafa',
    'lata'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro quando campo for modificado
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleImageChange = (file, error) => {
    setFormData(prev => ({
      ...prev,
      image: file
    }));
    
    if (error) {
      setErrors(prev => ({
        ...prev,
        image: error
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        image: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.price) {
      newErrors.price = 'Preço é obrigatório';
    } else if (parseFloat(formData.price) <= 0) {
      newErrors.price = 'Preço deve ser maior que zero';
    }

    if (formData.cost && parseFloat(formData.cost) < 0) {
      newErrors.cost = 'Custo não pode ser negativo';
    }

    if (!formData.category) {
      newErrors.category = 'Categoria é obrigatória';
    }

    if (!formData.stock_quantity) {
      newErrors.stock_quantity = 'Quantidade em estoque é obrigatória';
    } else if (parseInt(formData.stock_quantity) < 0) {
      newErrors.stock_quantity = 'Quantidade não pode ser negativa';
    }

    if (!formData.min_stock) {
      newErrors.min_stock = 'Estoque mínimo é obrigatório';
    } else if (parseInt(formData.min_stock) < 0) {
      newErrors.min_stock = 'Estoque mínimo não pode ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      // Converter campos numéricos e tratar código de barras vazio
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        cost: formData.cost ? parseFloat(formData.cost) : null,
        stock_quantity: parseInt(formData.stock_quantity),
        min_stock: parseInt(formData.min_stock),
        barcode: formData.barcode?.trim() || null // Converter string vazia para null
      };

      await onSubmit(productData);
    } catch (error) {
      console.error('Error submitting product:', error);
      toast.error('Erro ao salvar produto');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return parseFloat(value).toFixed(2);
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Package className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Editar Produto' : 'Cadastrar Novo Produto'}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações básicas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Informações Básicas
              </h3>
              
              <Input
                label="Nome do Produto *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="Ex: Lanche Natural"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descrição opcional do produto"
                />
              </div>

              <ImageUpload
                label="Foto do Produto"
                value={formData.image_url}
                onChange={handleImageChange}
                error={errors.image}
                placeholder="Adicionar foto do produto"
                accept="image/*"
                maxSize={5 * 1024 * 1024} // 5MB
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.category}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidade
                  </label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {units.map(unit => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Input
                label="Código de Barras"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                error={errors.barcode}
                placeholder="Código de barras (opcional)"
                icon={Hash}
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Preços e Estoque
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Preço de Venda *"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  error={errors.price}
                  placeholder="0,00"
                  icon={DollarSign}
                  required
                />

                <Input
                  label="Custo (opcional)"
                  name="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost}
                  onChange={handleChange}
                  error={errors.cost}
                  placeholder="0,00"
                  icon={DollarSign}
                />
              </div>

              {formData.price && formData.cost && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Margem de Lucro:</strong> {' '}
                    {((((parseFloat(formData.price) - parseFloat(formData.cost)) / parseFloat(formData.price)) * 100) || 0).toFixed(1)}%
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Quantidade em Estoque *"
                  name="stock_quantity"
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={handleChange}
                  error={errors.stock_quantity}
                  placeholder="0"
                  icon={BarChart3}
                  required
                />

                <Input
                  label="Estoque Mínimo *"
                  name="min_stock"
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={handleChange}
                  error={errors.min_stock}
                  placeholder="0"
                  icon={AlertCircle}
                  required
                />
              </div>

              {formData.stock_quantity && formData.min_stock && 
               parseInt(formData.stock_quantity) <= parseInt(formData.min_stock) && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Estoque atual está no limite ou abaixo do estoque mínimo
                  </p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                  Produto ativo (disponível para venda)
                </label>
              </div>
            </div>
          </div>

          {/* Resumo do produto */}
          {formData.name && formData.price && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Resumo do Produto</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Nome:</span>
                  <p className="font-medium text-blue-900">{formData.name}</p>
                </div>
                <div>
                  <span className="text-blue-700">Preço:</span>
                  <p className="font-medium text-blue-900">
                    R$ {formatCurrency(formData.price)}
                  </p>
                </div>
                <div>
                  <span className="text-blue-700">Categoria:</span>
                  <p className="font-medium text-blue-900">{formData.category || 'Não definida'}</p>
                </div>
                <div>
                  <span className="text-blue-700">Estoque:</span>
                  <p className="font-medium text-blue-900">
                    {formData.stock_quantity || 0} {formData.unit}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : (initialData ? 'Atualizar Produto' : 'Cadastrar Produto')}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
};

export default ProductForm;